import { defineStore } from 'pinia';
import { logService } from '../services/log.service';

export const useLogStore = defineStore('log', {
    state: () => ({
        databases: [],
        selectedDatabase: null,
        selectedCollection: null,
        collections: [],
        logs: [],
        stats: null,
        loading: false,
        error: null,
        pagination: {
            total: 0,
            currentPage: 1,
            limit: 10
        },
        viewMode: 'selection', // 'selection', 'stack', 'services'
        imeiStats: {
            lastUpdated: null,
            data: null,
            loading: false,
            error: null
        }
    }),

    getters: {
        hasError: (state) => !!state.error,
        isLoading: (state) => state.loading,
        getSelectedDatabase: (state) => state.selectedDatabase,
        getSelectedCollection: (state) => state.selectedCollection,
        getCollections: (state) => state.collections,
        getDatabases: (state) => state.databases,
        getLogs: (state) => state.logs,
        getStats: (state) => state.stats,
        getPagination: (state) => state.pagination,
        getViewMode: (state) => state.viewMode,
        
        // Nuovo getter per separare i database stack dai servizi
        getStackDatabases: (state) => {
            return state.databases.filter(db => db.name.includes('_stack'));
        },
        
        // Nuovo getter per i database dei servizi
        getServiceDatabases: (state) => {
            return state.databases.filter(db => !db.name.includes('_stack'));
        },
        getImeiStats: (state) => state.imeiStats,
        getTopImeis: (state) => {
            if (!state.imeiStats.data) return [];
            return Object.entries(state.imeiStats.data)
                .sort((a, b) => b[1].totalLogs - a[1].totalLogs)
                .slice(0, 10);
        }
    },

    actions: {
        setLoading(value) {
            this.loading = value;
        },

        async fetchDatabases() {
            try {
                this.loading = true;
                const response = await logService.getDatabases();
                if (response.success) {
                    this.databases = response.data.result;
                    
                    // Se c'è una selezione salvata in localStorage, la ripristiniamo
                    const savedDb = localStorage.getItem('selectedDatabase');
                    if (savedDb && this.databases.some(db => db.name === savedDb)) {
                        this.selectedDatabase = this.databases.find(db => db.name === savedDb);
                        await this.fetchCollections(savedDb);
                    }
                }
            } catch (error) {
                this.error = error.message;
            } finally {
                this.loading = false;
            }
        },

        async fetchCollections(dbName) {
            try {
                this.loading = true;
                const response = await logService.getCollections(dbName);
                if (response.success) {
                    this.collections = response.data.result;
                    
                    // Se c'è una selezione salvata in localStorage, la ripristiniamo
                    const savedCollection = localStorage.getItem('selectedCollection');
                    if (savedCollection && this.collections.some(col => col.name === savedCollection)) {
                        this.selectedCollection = this.collections.find(col => col.name === savedCollection);
                    }
                }
            } catch (error) {
                this.error = error.message;
            } finally {
                this.loading = false;
            }
        },

        setSelectedDatabase(database) {
            this.selectedDatabase = database;
            if (database) {
                localStorage.setItem('selectedDatabase', database.name);
            } else {
                localStorage.removeItem('selectedDatabase');
            }
        },

        setSelectedCollection(collection) {
            this.selectedCollection = collection;
            if (collection) {
                localStorage.setItem('selectedCollection', collection.name);
            } else {
                localStorage.removeItem('selectedCollection');
            }
        },

        clearSelections() {
            this.selectedDatabase = null;
            this.selectedCollection = null;
            localStorage.removeItem('selectedDatabase');
            localStorage.removeItem('selectedCollection');
        },

        async searchLogs(dbName, params) {
            try {
                this.loading = true;
                if (!dbName) {
                    throw new Error('Database non selezionato');
                }
                console.log('Search params:', params); // Debug log
                const response = await logService.searchLogs(dbName, params);
                if (response.success) {
                    this.logs = response.data.result;
                    if (response.data.pagination) {
                        this.pagination = response.data.pagination;
                    }
                }
                return response;
            } catch (error) {
                this.error = error.message;
                console.error('Errore nella ricerca dei log:', error);
                throw error;
            } finally {
                this.loading = false;
            }
        },

        async fetchStats(dbName, params) {
            try {
                this.loading = true;
                const response = await logService.getStats(dbName, params);
                if (response.success) {
                    this.stats = response.data.result;
                }
            } catch (error) {
                this.error = error.message;
            } finally {
                this.loading = false;
            }
        },

        async exportToCSV(params) {
            try {
                this.loading = true;
                const response = await logService.exportToCSV(this.selectedDatabase.name, params);
                // Gestione del download del file CSV
                const blob = new Blob([response], { type: 'text/csv' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `log_export_${new Date().toISOString()}.csv`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            } catch (error) {
                this.error = error.message;
            } finally {
                this.loading = false;
            }
        },

        clearError() {
            this.error = null;
        },

        setViewMode(mode) {
            if (['selection', 'stack', 'services'].includes(mode)) {
                this.viewMode = mode;
                // Reset delle selezioni quando si cambia modalità
                this.clearSelections();
            }
        },

        async fetchImeiStats(dbName) {
            try {
                this.imeiStats.loading = true;
                this.imeiStats.error = null;

                const collections = ['parser', 'comunicator', 'event_device'];
                const stats = {};

                const searchParams = {
                    search: '',
                    collections,
                    deepSearch: true,
                    noLimitResult: true,
                    fields: ['imei', '_collection', 'timestamp', 'metadata.lat', 'metadata.lng', 'metadata.gpsAccuracy', 'metadata.data.fields.lat', 'metadata.data.fields.lng', 'metadata.data.params.gpsAccuracy']
                };

                const response = await this.searchLogs(dbName, searchParams);
                
                if (response?.data?.result) {
                    response.data.result.forEach(log => {
                        const imei = log.imei || log.metadata?.imei || log.metadata?.data?.fields?.imei;
                        
                        // Valida l'IMEI
                        if (!imei || typeof imei !== 'string' || !/^\d{10,15}$/.test(imei)) return;

                        if (!stats[imei]) {
                            stats[imei] = {
                                totalLogs: 0,
                                lastSeen: {},
                                collections: {
                                    parser: 0,
                                    comunicator: 0,
                                    event_device: 0
                                },
                                lastPosition: null
                            };
                        }

                        stats[imei].collections[log._collection]++;
                        stats[imei].totalLogs++;

                        const timestamp = new Date(log.timestamp);
                        if (!stats[imei].lastSeen[log._collection] || 
                            timestamp > new Date(stats[imei].lastSeen[log._collection])) {
                            stats[imei].lastSeen[log._collection] = log.timestamp;

                            // Aggiorna la posizione se il log contiene coordinate valide
                            if (log._collection === 'parser' || log._collection === 'event_device') {
                                const lat = log.metadata?.lat || log.metadata?.data?.fields?.lat;
                                const lng = log.metadata?.lng || log.metadata?.data?.fields?.lng;
                                const accuracy = log.metadata?.gpsAccuracy || log.metadata?.data?.params?.gpsAccuracy;

                                if (typeof lat === 'number' && typeof lng === 'number') {
                                    stats[imei].lastPosition = {
                                        lat,
                                        lng,
                                        accuracy: accuracy || 10,
                                        timestamp: log.timestamp
                                    };
                                }
                            }
                        }
                    });

                    this.imeiStats.data = stats;
                    this.imeiStats.lastUpdated = new Date();
                }
            } catch (error) {
                this.imeiStats.error = error.message;
                console.error('Errore nel recupero delle statistiche IMEI:', error);
            } finally {
                this.imeiStats.loading = false;
            }
        }
    }
}); 